import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getAdminOptionsRequest,
  getStaffAdminRequest,
  getStaffAdminsRequest,
  updateStaffAdminPlanRequest,
} from './adminsApi';
import { extractApiError } from '../../utils/errors';

/** The staff's table of admins, for the filters currently in the store. */
export const fetchStaffAdmins = createAsyncThunk(
  'admins/fetchStaffAdmins',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { search, page, rowsPerPage } = getState().admins.filters;

      const params = { page, page_size: rowsPerPage };
      if (search) params.search = search;

      const response = await getStaffAdminsRequest(params);
      return response.data;
    } catch (error) {
      console.error('Error fetching admins:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener los admins'));
    }
  }
);

export const fetchStaffAdminDetail = createAsyncThunk(
  'admins/fetchStaffAdminDetail',
  async (adminId, { rejectWithValue }) => {
    try {
      const response = await getStaffAdminRequest(adminId);
      return response.data;
    } catch (error) {
      console.error('Error fetching admin detail:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo obtener el admin'));
    }
  }
);

/**
 * Turns one capability of an admin's plan on or off.
 *
 * The 200 carries the whole `admin_plan`, which is what the screen renders
 * afterwards: the switch shows what the server stored, never what was clicked.
 * A rejected call therefore leaves it where it was, with no state to roll back.
 *
 * @param {{adminId: number|string, field: 'seller_channels'|'addons', key: string, value: boolean}} args
 */
export const updateStaffAdminPlan = createAsyncThunk(
  'admins/updateStaffAdminPlan',
  async ({ adminId, field, key, value }, { rejectWithValue }) => {
    try {
      const response = await updateStaffAdminPlanRequest(adminId, field, key, value);
      return { adminPlan: response.data, field, key };
    } catch (error) {
      console.error('Error updating admin plan:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo actualizar el plan'));
    }
  },
  // One switch per call, so two switches saving at once each wait for their own.
  { getPendingMeta: ({ arg }) => ({ pendingKey: `${arg.field}.${arg.key}` }) }
);

/**
 * The roster of admins as select options, for the screens that name one: the
 * operator filter of the listings and the seller the staff is creating.
 *
 * Fetched once per session — it is a catalog — and mapped the same way the zone
 * form does it, so the same admin reads identically in both selects.
 */
export const fetchAdminOptions = createAsyncThunk(
  'admins/fetchAdminOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAdminOptionsRequest();

      return (response.data || []).map((admin) => ({
        value: admin.id,
        label: `${admin.user?.email} (${admin.user?.first_name} ${admin.user?.last_name})`,
        // Whether it can receive somebody else's shipments, which is what an
        // alliance asks of its destination.
        allowsUpselling: admin.allows_upselling === true,
        // Only the checkout plan has a fleet of its own, and therefore drivers
        // to assign. Staff reads the plan of the operator it is looking at, not
        // its own.
        planType: admin.admin_plan?.plan_type ?? null,
      }));
    } catch (error) {
      console.error('Error fetching admin options:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo obtener la lista de admins'));
    }
  },
  {
    condition: (_, { getState }) => !getState().admins.optionsLoaded,
  }
);
